import {
  Clonable,
  DEFAULT_TEAM_SEATS,
  Subscription,
  StorageFolder,
  TeamSettings,
  UNLIMITED_SEATS,
  TeamMetadata,
  getTeamFirebaseStoragePath,
  FormerUser,
} from '../../..';
import { TeamTrial } from './team-trial.model';

export type TeamWithDomain = Team & Required<Pick<Team, 'domain'>>;
export type TeamWithSubscription = Team & Required<Pick<Team, 'subscription'>>;
export type TeamWithTrial = Team & Required<Pick<Team, 'trial'>>;

export class Team extends Clonable {
  constructor(
    public readonly id: string,
    public readonly createdAt: Date,
    public readonly createdBy: string,
    public readonly folder: StorageFolder,
    public readonly formerUsers: Map<string, FormerUser>,
    public readonly industryId: string,
    public readonly metadata: TeamMetadata | undefined,
    public readonly name: string,
    public readonly ownerId: string,
    public readonly settings: TeamSettings,
    public readonly domain?: string,
    public readonly additionalDomains?: string[],
    private readonly seatsOverride?: number,
    public readonly subscription?: Subscription,
    public readonly trial?: TeamTrial,
    /** Allow importing external users via CSV bulk import. Support prop. */
    public readonly allowExternalUsersBulkImport?: boolean,
  ) {
    super();
  }

  get domains(): string[] {
    return [...(this.domain ? [this.domain] : []), ...(this.additionalDomains ?? [])].sort();
  }

  get seats(): number {
    if (this.isSubscribed()) {
      return this.seatsOverride ?? this.subscription.seats;
    } else {
      return DEFAULT_TEAM_SEATS;
    }
  }

  /** Returns Firebase Storage path for the public album logo */
  public getTeamLogoPath(): string {
    return getTeamFirebaseStoragePath(this.id, this.settings['team.logo'] ?? '');
  }

  public hasAnyDomain(): boolean {
    return this.hasDomain() || this.hasAdditionalDomains();
  }

  public hasDomain(): this is TeamWithDomain {
    return !!this.domain?.trim();
  }

  public hasAdditionalDomains(): boolean {
    return (this.additionalDomains ?? []).length > 0;
  }

  public hasTeamLogo(): boolean {
    return !!this.settings['team.logo'];
  }

  public hasTrial(): this is TeamWithTrial {
    return !!this.trial;
  }

  public isSubscribed(): this is TeamWithSubscription {
    return !!this.subscription?.isValid();
  }

  public hasUnlimitedSeats(): boolean {
    return this.seats === UNLIMITED_SEATS;
  }
}
